/**
 * sessionStorage helpers for patient dashboard layout signatures and draft
 * cleanup. Reload does not restore session deltas or retry PUT; any stale draft
 * is cleared when {@link DashboardComponent} mounts.
 */

const DRAFT_KEY_PREFIX = 'smartchart:widget-layout-draft:';

export type PatientDashboardLayoutDraft = {
  added: mumms.WidgetConfig[];
  removed: mumms.WidgetConfig[];
  /** Server baseline (`config` prop) when the draft was written. */
  baselineLayoutSignature: string;
  /** Optimistic merged layout at write time (debug / future validation). */
  mergedLayoutSignature: string;
};

/** Stable fingerprint for layout rows (component, order, doNotShow). */
export function buildPatientDashboardLayoutSignature(
  layout: mumms.WidgetConfig[],
): string {
  return JSON.stringify(
    layout.map((w) => ({
      c: w.componentName,
      o: w.widgetMenuOrder,
      d: w.doNotShow,
    })),
  );
}

export function getWidgetLayoutDraftKey(
  agency: string | undefined,
  userId: string | undefined,
): string | null {
  if (!agency || !userId) {
    return null;
  }
  return `${DRAFT_KEY_PREFIX}${agency}:${userId}`;
}

function isWidgetConfigArray(value: unknown): value is mumms.WidgetConfig[] {
  return Array.isArray(value);
}

export function readWidgetLayoutDraft(
  draftKey: string,
): PatientDashboardLayoutDraft | null {
  try {
    const raw = sessionStorage.getItem(draftKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<PatientDashboardLayoutDraft>;
    if (
      !isWidgetConfigArray(parsed.added) ||
      !isWidgetConfigArray(parsed.removed) ||
      typeof parsed.baselineLayoutSignature !== 'string' ||
      typeof parsed.mergedLayoutSignature !== 'string'
    ) {
      return null;
    }
    return {
      added: parsed.added,
      removed: parsed.removed,
      baselineLayoutSignature: parsed.baselineLayoutSignature,
      mergedLayoutSignature: parsed.mergedLayoutSignature,
    };
  } catch {
    return null;
  }
}

export function writeWidgetLayoutDraft(
  draftKey: string,
  draft: PatientDashboardLayoutDraft,
): void {
  try {
    sessionStorage.setItem(draftKey, JSON.stringify(draft));
  } catch {
    // Quota or private mode — ignore; optimistic UI still works in memory.
  }
}

export function clearWidgetLayoutDraft(draftKey: string): void {
  try {
    sessionStorage.removeItem(draftKey);
  } catch {
    // ignore
  }
}

/** True when sessionStorage has a layout draft with add/remove deltas for this baseline. */
export function hasPendingPatientDashboardLayoutDraft(
  draftKey: string | null,
  baselineConfig: mumms.WidgetConfig[],
): boolean {
  if (!draftKey) {
    return false;
  }
  const draft = readWidgetLayoutDraft(draftKey);
  if (!draft || (!draft.added.length && !draft.removed.length)) {
    return false;
  }
  return (
    draft.baselineLayoutSignature ===
    buildPatientDashboardLayoutSignature(baselineConfig)
  );
}

export type RestoredPatientDashboardLayoutDraft = {
  added: mumms.WidgetConfig[];
  removed: mumms.WidgetConfig[];
};

/**
 * Read draft when baseline layout still matches the server `config` prop.
 * Stale drafts are cleared. Not used on reload (mount clears draft instead).
 */
export function restorePatientDashboardLayoutDraft(
  enabled: boolean,
  draftKey: string | null,
  baselineConfig: mumms.WidgetConfig[],
): RestoredPatientDashboardLayoutDraft {
  if (!enabled || !draftKey) {
    return { added: [], removed: [] };
  }

  const draft = readWidgetLayoutDraft(draftKey);
  if (!draft) {
    return { added: [], removed: [] };
  }

  const baselineSignature = buildPatientDashboardLayoutSignature(
    baselineConfig,
  );
  if (draft.baselineLayoutSignature !== baselineSignature) {
    clearWidgetLayoutDraft(draftKey);
    return { added: [], removed: [] };
  }

  if (!draft.added.length && !draft.removed.length) {
    clearWidgetLayoutDraft(draftKey);
    return { added: [], removed: [] };
  }

  return {
    added: draft.added,
    removed: draft.removed,
  };
}
